import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import checkIcon from '../../../../../../Assets/icons/check.svg';
import { DetailStepChip } from '../types';
import ChipCollage from './ChipCollage';

/** Rendered circle size - the CDN is asked for double for retina. */
const CIRCLE_PX = 80;

const Chip = styled.div`
  flex: 0 0 auto;
  width: 116px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0.55;
  transition: opacity 0.2s ease-out;

  &.is-current,
  &.is-done {
    opacity: 1;
  }
`;

/** Positions the badge against the circle - the circle itself clips, so the badge cannot live inside it. */
const CircleWrap = styled.div`
  position: relative;
  width: ${CIRCLE_PX}px;
  height: ${CIRCLE_PX}px;
`;

/** Icon or pick photo. The ring says "you are here"; the badge says "picked". */
const Circle = styled.div`
  width: ${CIRCLE_PX}px;
  height: ${CIRCLE_PX}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--eposCategoryBackground);
  color: var(--eposCategoryText);
  font-size: 24px;
  font-weight: 800;
  overflow: hidden;
  box-shadow: 0 0 0 2px var(--eposBorderColor);
  transition: box-shadow 0.2s ease-out;

  .is-current & {
    box-shadow: 0 0 0 3px var(--eposButton);
  }
`;

/** Sits over the circle's bottom-right edge, outside the clipped circle: a check, or the count when more than one is picked. */
const Badge = styled.span`
  position: absolute;
  right: -2px;
  bottom: -2px;
  min-width: 26px;
  height: 26px;
  padding: 0 7px;
  box-sizing: border-box;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--eposButton);
  color: var(--eposButtonText);
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 0 0 2px var(--eposSurface);
`;

const Label = styled.span`
  max-width: 100%;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  color: var(--eposText);
  overflow: hidden;

  /* Over the hero photo. */
  .is-step & {
    color: var(--colorWhite);
    text-shadow: 0 1px 6px rgba(0, 0, 0, 0.4);
  }
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface Props {
  chip: DetailStepChip;
  /** 1-based, drawn when the step has neither icon nor pick photo. */
  position: number;
  current: boolean;
  /** Forwarded so the parent can scroll the current chip into view. */
  chipRef?: (node: HTMLDivElement | null) => void;
}

/** One step: a collage of the picks' photos if something is picked, else the group icon, else the step number. */
const StepChip: FC<Props> = memo(({ chip, position, current, chipRef }) => {
  const done = chip.pickCount > 0;
  const showPhotos = done && chip.imageUrls.length > 0;
  const className = [current && 'is-current', done && 'is-done'].filter(Boolean).join(' ');

  return (
    <Chip
      ref={chipRef}
      className={className || undefined}
      data-testid="epos-step-chip"
      data-step-done={done || undefined}
      data-step-count={done ? chip.pickCount : undefined}>
      <CircleWrap>
        <Circle>
          {showPhotos ? (
            <ChipCollage imageUrls={chip.imageUrls} sizePx={CIRCLE_PX} />
          ) : chip.iconSrc ? (
            <Icon
              src={chip.iconSrc}
              color="var(--eposCategoryText)"
              width="55%"
              height="55%"
              center
            />
          ) : (
            position
          )}
        </Circle>
        {done && (
          <Badge>
            {chip.pickCount > 1 ? (
              chip.pickCount
            ) : (
              <Icon
                src={checkIcon}
                color="var(--eposButtonText)"
                width="15px"
                height="15px"
                center
              />
            )}
          </Badge>
        )}
      </CircleWrap>
      <Label>{chip.label}</Label>
    </Chip>
  );
});

StepChip.displayName = 'StepChip';

export default StepChip;
